/* eslint-disable complexity */
import { createNamespacedHelpers } from "vuex";
import moment from "moment";
import filterData from "@/utils.js";
import { Fragment } from "vue-frag";
import { ChevronDownIcon, ChevronRightIcon, EditIcon } from "vue-feather-icons";
import { serviceTimeRangeToString } from "@/utils";

const { mapGetters: userGetters } = createNamespacedHelpers("user"),
	{ mapGetters: filtersGetters } = createNamespacedHelpers("filters");

export default {
	props: {
		current: {
			type: Object,
			required: true,
		},
		compare: {
			type: Object,
			required: true,
		},
	},

	data: () => ({
		stColumns: 3,
		currentExpanded: false,
		compareExpanded: false,
		lastSTAdded: -1,
	}),

	methods: {
		getTotalsSetIDs,
		getVisibleSetIDs,
		getPeriodTitle,
		getPeriodTitleComparator,
		formatDate,
		isWeekend,
		isAverage,
		handleDataRowClick,
		addServiceTimeSetToRow,
		parseRows,
		openGuestCount,
		serviceTimeRangeToString,
	},

	components: {
		Fragment,
		ChevronDownIcon,
		ChevronRightIcon,
		EditIcon,
	},

	async created() {
		const stSets = new Set();
		this.getVisibleSetIDs().forEach((set) => stSets.add(set));
		if (this.compare.success) this.getVisibleSetIDs(true).forEach((set) => stSets.add(set));

		// set the number of columns
		const MIN_COLUMNS = 3;
		this.stColumns = MIN_COLUMNS;

		stSets.forEach((id) => {
			const columns = this.serviceTimeSets[id].st_labels.length;

			this.stColumns = Math.max(columns, this.stColumns);
		});
	},

	computed: {
		...userGetters(["activeOutlet", "serviceTimeSets"]),
		...filtersGetters(["activePeriod", "activeDate", "activeCompareTo"]),
		rows,
	},
};

// TODO: document
function getTotalsSetIDs(compare) {
	if (compare && !this.compare.success) return [];

	const data = (compare ? this.compare : this.current).data.totals;
	return Object.keys(data.by_sts || {})
		.map((id) => parseInt(id))
		.sort();
}

// TODO: document
function getVisibleSetIDs(compare) {
	if (compare && !this.compare.success) return [];

	const sets = new Set();

	// let's iterate through the rows and check how many sets we will render
	for (const row of [...(compare ? this.compare.data.rows : this.current.data.rows)]) {
		// we only shows rows with one STS!
		const rowSets = Object.keys(row.by_sts || {});
		if (rowSets.length === 1) sets.add(parseInt(rowSets[0]));
	}

	return Array.from(sets).sort();
}

function addServiceTimeSetToRow(rows, row, stsId) {
	stsId = parseInt(stsId);
	if (stsId !== this.lastSTSAdded) {
		this.lastSTSAdded = stsId;

		// if it's the main row, nothing to do
		// otherwise, we want to create a new header row
		if (!row.main) {
			rows.push({
				type: "header",
				serviceTimeSet: this.serviceTimeSets[this.lastSTSAdded],
			});
		}
	}

	row.serviceTimeSet = this.serviceTimeSets[this.lastSTSAdded];
}

function parseRows(rows, data, compare) {
	// add totals row
	const totals = {
		type: "totals",
		compare: compare,
		...data.totals,
	};

	const totalsSetIDs = this.getTotalsSetIDs(compare);

	if (totalsSetIDs.length === 1) this.addServiceTimeSetToRow(rows, totals, totalsSetIDs[0]);

	rows.push(totals);

	// add data rows
	if (compare ? this.compareExpanded : this.currentExpanded) {
		// we want to show the table rows in descending order
		const toParse = Object.values(data.rows).reverse();

		toParse.forEach((row) => {
			const obj = { compare: compare, ...row };
			obj.date = this.isAverage(obj) ? parseInt(obj.date) : moment(obj.date);

			// if there is only service time add the data split by ST
			// if the row is also an average, the overall period must have only
			// ONE service time set defined, otherwise the data looks confusing
			const setIDs = Object.keys(obj.by_sts || {});
			if (setIDs.length === 1 && (!this.isAverage(obj) || totalsSetIDs.length === 1))
				this.addServiceTimeSetToRow(rows, obj, setIDs[0]);

			rows.push(obj);
		});
	}
}

function rows() {
	const rows = [];

	// add main header
	const mainRow = { type: "header", main: true };

	// if there is only one service time set, add it to the main row
	if (this.getTotalsSetIDs().length === 1) this.addServiceTimeSetToRow(rows, mainRow, this.getTotalsSetIDs()[0]);
	else if (!this.currentExpanded && this.compare.success && this.getTotalsSetIDs(true).length === 1) {
		// if there are multiple service time sets, let's peek the 'compare' totals
		this.addServiceTimeSetToRow(rows, mainRow, this.getTotalsSetIDs(true)[0]);
	}

	rows.push(mainRow);

	this.parseRows(rows, this.current.data, false);

	if (!this.compare.success) return rows;

	this.parseRows(rows, this.compare.data, true);

	return rows;
}

function handleDataRowClick(row) {
	if (row.type !== "totals") return;

	if (row.compare) this.compareExpanded = !this.compareExpanded;
	else this.currentExpanded = !this.currentExpanded;

	// let's ensure that we don't have any leftovers for next render...
	this.lastSTAdded = -1;
}

/*
 * Returns true if this expandable row shows average values
 * (ie. comparators to avg or base periods)
 */
function isAverage(row) {
	// eslint-disable-next-line no-extra-parens
	return (
		row.compare &&
		typeof this.activeCompareTo === "string" &&
		(this.activeCompareTo.startsWith("base") || this.activeCompareTo.startsWith("average"))
	);
}

/*
 * Returns the date in the format in which we want to show it,
 * depending what period and what comparator are chosen
 */
function formatDate(date, compare) {
	if (this.activePeriod === "year") return date.format("MMMM");
	else if (compare) {
		if (["base_week", "average_week"].includes(this.activeCompareTo)) {
			const daysOfWeek = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
			// date is a number between 1 and 7
			return this.$t("days." + daysOfWeek[date - 1]);
		} else if (["base_month", "average_month"].includes(this.activeCompareTo))
			return `${this.$t("filters_period.day")} ${date}`;
		else if (["base_day", "average_day"].includes(this.activeCompareTo)) return "";
	}
	return date.format("ddd. DD.MM.YYYY");
}

/*
 * Returns true if the given date is during a weekend
 */
function isWeekend(row) {
	const WEEK = 7;
	if (this.activePeriod === "month" && (!row.compare || this.activeCompareTo === "previous_month"))
		return [WEEK - 1, 0].includes(row.date.day());
	else if (this.activePeriod === "week") {
		// when comparing to avg or base, the date is an int (day of week), otherwise moment object
		return [WEEK - 1, 0].includes(typeof row.date === "number" ? row.date % WEEK : row.date.day() % WEEK);
	}
	return false;
}

function getPeriodTitle() {
	switch (this.activePeriod) {
		case "day":
			return this.activeDate.format("DD.MM.YYYY");
		case "week":
			return `${this.$t("filters_period.week")} ${this.activeDate.week()}`;
		case "month":
			return this.activeDate.format("MMMM");
		case "year":
			return this.activeDate.format("YYYY");
	}
}

// eslint-disable-next-line no-magic-numbers
function getPeriodTitleComparator() {
	if (this.activeCompareTo === "previous_month") return moment(this.activeDate).subtract(1, "month").format("MMMM");
	else if (this.activeCompareTo === "previous_week")
		return `${this.$t("filters_period.week")} ${moment(this.activeDate).subtract(1, "week").week()}`;
	else if (this.activeCompareTo === "day_before")
		return moment(this.activeDate).subtract(1, "day").format("DD.MM.YYYY");
	else if (this.activeCompareTo === "same_day_week_before") {
		return (
			moment(this.activeDate)
				// eslint-disable-next-line no-magic-numbers
				.subtract(7, "day")
				.format("DD.MM.YYYY")
		);
	} else if (["average_month", "base_month"].includes(this.activeCompareTo))
		return this.$t("filters_compare_month." + this.activeCompareTo);
	else if (["average_week", "base_week"].includes(this.activeCompareTo))
		return this.$t("filters_compare_week." + this.activeCompareTo);
	else if (["average_day", "base_day"].includes(this.activeCompareTo))
		return this.$t("filters_compare_day." + this.activeCompareTo);
	else if (this.activePeriod === "year") return moment(this.activeCompareTo + "-01-01").format("YYYY");
}

/*
 * Opens the guest count settings in a new tab.
 *
 * If we are in comparator mode and comparing to average or base period,
 * the guest count settings of the month of the beginning of the comparator
 * period should be opened.
 */
function openGuestCount(row) {
	const openDate = this.isAverage(row)
			? moment(filterData.returnFiltersState({}).dates.start_date_compare, "dd/MM/YYYY")
			: row.date,
		month = openDate.format("YYYY/MM"),
		route = this.$router.resolve({ name: "guestCount" });

	sessionStorage.guestCountMonth = month;
	window.open(route.href);
}
